import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DASHBOARD_KEYS,
  type DashboardKey,
  extractListStateFromLocation,
  selectedApplicationsFromWire,
  selectedIdsFromWire,
} from '../utils/listNavigationState';

type SortConfig = {
  field: string | null;
  direction: 'asc' | 'desc' | null;
};

type RestoreDefaults = {
  page?: number;
  searchText?: string;
  activeFilters?: Record<string, string>;
  selectedIds?: Set<number>;
  selectedApplications?: Set<number>;
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
};

type RestoreListStateResult = {
  page: number;
  searchText: string;
  activeFilters: Record<string, string>;
  selectedIds: Set<number>;
  selectedApplications: Set<number>;
  sortConfig: SortConfig;
  restored: boolean;
};

function buildRestoreResult(
  dashboardKey: DashboardKey,
  locationState: unknown,
  defaults: RestoreDefaults,
): RestoreListStateResult {
  const defaultPage = defaults.page ?? 0;
  const defaultSearchText = defaults.searchText ?? '';
  const defaultActiveFilters = defaults.activeFilters ?? {};
  const defaultSelectedIds = defaults.selectedIds ?? new Set<number>();
  const defaultSelectedApplications = defaults.selectedApplications ?? new Set<number>();
  const defaultSortConfig: SortConfig = {
    field: defaults.sortField ?? null,
    direction: defaults.sortDirection ?? null,
  };

  const restoredState = extractListStateFromLocation(locationState, dashboardKey);

  if (!restoredState) {
    return {
      page: defaultPage,
      searchText: defaultSearchText,
      activeFilters: defaultActiveFilters,
      selectedIds: defaultSelectedIds,
      selectedApplications: defaultSelectedApplications,
      sortConfig: defaultSortConfig,
      restored: false,
    };
  }

  return {
    page: restoredState.page,
    searchText: restoredState.searchText,
    activeFilters: restoredState.activeFilters,
    selectedIds: restoredState.selectedIds
      ? selectedIdsFromWire(restoredState.selectedIds)
      : defaultSelectedIds,
    selectedApplications: restoredState.selectedApplications
      ? selectedApplicationsFromWire(restoredState.selectedApplications)
      : defaultSelectedApplications,
    sortConfig: {
      field: restoredState.sortField ?? null,
      direction: restoredState.sortDirection ?? null,
    },
    restored: true,
  };
}

export function useRestoreListState(
  dashboardKey: DashboardKey,
  defaults: RestoreDefaults = {},
): RestoreListStateResult {
  const location = useLocation();
  const navigate = useNavigate();
  const consumedRef = useRef(false);

  const [restoreResult] = useState(() =>
    buildRestoreResult(dashboardKey, location.state, defaults),
  );

  useEffect(() => {
    if (!restoreResult.restored || consumedRef.current) {
      return;
    }

    consumedRef.current = true;
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, navigate, restoreResult.restored]);

  return restoreResult;
}

export { DASHBOARD_KEYS };
